import React from 'react';
import { i18n } from '../../i18n/i18n';
import { useLanguage } from '../../context/LanguageContext';
import { parseSchedule, ymd } from '../../utils/repeatSchedule';
import { pluralKey } from '../../utils/starGoal';
import { fieldsForRepeatMode, repeatModeOf, repeatSummary, RepeatMode, DEFAULT_WITHIN_DAYS } from '../../utils/repeatSettings';

/**
 * Task editor: deadline + "When it's approved…" (Jens 2026-10-13). Every choice reads as a
 * sentence, and "What happens" spells out the result, so parents don't have to work out how
 * separate number fields combine (they used to: "repeat every 7" + "7 days to complete" meant
 * "available again at once").
 */
type Data = {
  assigned_to?: string;
  auto_available?: boolean;
  recreate_on_validation?: boolean;
  recurrence_days?: number;
  days_to_complete?: number | null;
  due_date?: Date;
  next_series_id?: string | null;
  next_wait_hours?: number | null;
  repeat_schedule?: any;
};

const inputCls = 'w-20 min-h-[44px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-2 text-base text-center focus:outline-none focus:border-kid-green';
const fieldCls = 'w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green';
const days = (v: string) => Math.max(1, parseInt(v, 10) || 1);

/** yyyy-mm-dd in local time (toISOString would show the UTC date, a day off in the evening). */
const toDateInput = (d?: Date) => {
  if (!d || Number.isNaN(new Date(d).getTime())) return '';
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const Choice: React.FC<{ checked: boolean; onSelect: () => void; name: string; children: React.ReactNode }> = ({ checked, onSelect, name, children }) => (
  <label className="flex items-center gap-3 min-h-[44px] text-base cursor-pointer">
    <input type="radio" name={name} checked={checked} onChange={onSelect} className="w-6 h-6 shrink-0 accent-kid-green" />
    <span className="flex flex-wrap items-center gap-2">{children}</span>
  </label>
);

const RepeatSettings: React.FC<{
  data: Data;
  members: Array<{ id: string; name: string }>;
  /** Tasks that can come next (chains): one per task, not this task itself. */
  nextChoices: Array<{ series: string; title: string }>;
  /** Shared tasks: everyone it is for (each gets their own copy). */
  assigneeNames?: string[];
  /** Master Admin's standard tasks: no start dates (a plan starts the day the task is used). */
  forTemplate?: boolean;
  onChange: (patch: Partial<Data>) => void;
}> = ({ data, members, nextChoices, assigneeNames = [], forTemplate = false, onChange }) => {
  const mode = repeatModeOf(data);
  const within = Number(data.days_to_complete) > 0 ? Number(data.days_to_complete) : 0;
  const assignee = members.find((m) => m.id === data.assigned_to);
  const setMode = (m: RepeatMode) => onChange(fieldsForRepeatMode(m, data, nextChoices[0]?.series ?? null));
  const nextTitle = nextChoices.find((c) => c.series === data.next_series_id)?.title ?? null;
  const { current_language } = useLanguage();
  // Weekday names, index 1 = Monday … 7 = Sunday (5 Jan 2026 was a Monday).
  const weekday = (style: 'short' | 'long') => ['', ...[5, 6, 7, 8, 9, 10, 11].map((d) =>
    new Date(2026, 0, d).toLocaleDateString(current_language, { weekday: style }))];
  const shortDays = weekday('short');
  const plan = parseSchedule(data.repeat_schedule);
  // A standard task (Master Admin) has no start date: "every 2nd …" counts from the day it is used.
  const start = (): { from?: string } => (forTemplate ? {} : { from: ymd(new Date()) });
  const setPlan = (p: any) => onChange({ ...fieldsForRepeatMode('plan', data), repeat_schedule: p });
  const today = new Date().getDay() || 7;
  const toggleDay = (d: number) => {
    if (plan?.kind !== 'weekly') return;
    const next = plan.days.includes(d) ? plan.days.filter((x) => x !== d) : [...plan.days, d].sort();
    if (next.length) setPlan({ ...plan, days: next });
  };
  const pickCls = `${fieldCls} !w-auto`;
  const longDays = weekday('long');
  const everyOptions = (key: string, values: number[]) => values.map((n) => (
    <option key={n} value={n}>{i18n.t(pluralKey(key, n)).replace('{{n}}', String(n))}</option>
  ));

  return (
    <div className="w-full max-w-md flex flex-col gap-5">
      <fieldset>
        <legend className="block text-base font-bold mb-1">{i18n.t('admin_panel.deadline_question')}</legend>
        <Choice name="deadline" checked={!within} onSelect={() => onChange({ days_to_complete: 0 })}>
          {i18n.t('admin_panel.deadline_none')}
        </Choice>
        <Choice name="deadline" checked={!!within} onSelect={() => onChange({ days_to_complete: within || DEFAULT_WITHIN_DAYS })}>
          {i18n.t('admin_panel.deadline_within')}
          <input type="number" min={1} aria-label={i18n.t('admin_panel.deadline_within')} className={inputCls}
            value={within || ''} placeholder={String(DEFAULT_WITHIN_DAYS)}
            onFocus={() => !within && onChange({ days_to_complete: DEFAULT_WITHIN_DAYS })}
            onChange={(e) => onChange({ days_to_complete: days(e.target.value) })} />
          {i18n.t('admin_panel.days_unit')}
        </Choice>
      </fieldset>

      <fieldset>
        <legend className="block text-base font-bold mb-1">{i18n.t('admin_panel.repeat_question')}</legend>
        <Choice name="repeat" checked={mode === 'none'} onSelect={() => setMode('none')}>{i18n.t('admin_panel.repeat_none')}</Choice>
        <Choice name="repeat" checked={mode === 'now'} onSelect={() => setMode('now')}>{i18n.t('admin_panel.repeat_now')}</Choice>
        <Choice name="repeat" checked={mode === 'after'} onSelect={() => setMode('after')}>
          {i18n.t('admin_panel.repeat_after')}
          <input type="number" min={1} aria-label={i18n.t('admin_panel.repeat_after')} className={inputCls}
            value={mode === 'after' ? data.recurrence_days || '' : ''} placeholder="7"
            onFocus={() => mode !== 'after' && setMode('after')}
            onChange={(e) => onChange({ recreate_on_validation: true, recurrence_days: days(e.target.value) })} />
          {i18n.t('admin_panel.days_unit')}
        </Choice>
        <Choice name="repeat" checked={mode === 'plan'} onSelect={() => setMode('plan')}>{i18n.t('admin_panel.repeat_plan')}</Choice>
        {mode === 'plan' && (
          // A fixed plan, like a calendar app (2026-10-14/15): weekly on chosen days (every week or every
          // 2nd, 3rd …), every X days, or monthly on a date or e.g. the first Sunday.
          <div className="ml-9 mb-2 space-y-2">
            <Choice name="plan" checked={plan?.kind === 'weekly'} onSelect={() => setPlan({ kind: 'weekly', days: [today] })}>
              <select aria-label={i18n.t('admin_panel.plan_weekly')} className={pickCls}
                value={plan?.kind === 'weekly' ? plan.every || 1 : 1}
                onFocus={() => plan?.kind !== 'weekly' && setPlan({ kind: 'weekly', days: [today] })}
                onChange={(e) => {
                  const every = Number(e.target.value);
                  const days = plan?.kind === 'weekly' ? plan.days : [today];
                  setPlan(every > 1 ? { kind: 'weekly', days, every, ...start() } : { kind: 'weekly', days });
                }}>
                {everyOptions('admin_panel.plan_week_every', [1, 2, 3, 4, 6, 8])}
              </select>
              {i18n.t('admin_panel.plan_on')}
            </Choice>
            {plan?.kind === 'weekly' && (
              <div className="flex flex-wrap gap-1.5 ml-9" role="group" aria-label={i18n.t('admin_panel.plan_weekly')}>
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <button key={d} type="button" aria-pressed={plan.days.includes(d)} onClick={() => toggleDay(d)}
                    className={`min-w-[44px] min-h-[44px] px-2 rounded-full font-bold border-2 ${plan.days.includes(d)
                      ? 'bg-kid-green text-white border-kid-green'
                      : 'bg-kid-card dark:bg-kid-card-dark border-kid-border-strong dark:border-kid-border-strong-dark'}`}>
                    {shortDays[d]}
                  </button>
                ))}
              </div>
            )}
            <Choice name="plan" checked={plan?.kind === 'monthly'} onSelect={() => setPlan({ kind: 'monthly', nth: 1, weekday: today })}>
              <select aria-label={i18n.t('admin_panel.plan_monthly')} className={pickCls}
                value={plan?.kind === 'monthly' ? plan.every || 1 : 1}
                onFocus={() => plan?.kind !== 'monthly' && setPlan({ kind: 'monthly', nth: 1, weekday: today })}
                onChange={(e) => {
                  const every = Number(e.target.value);
                  const base = plan?.kind === 'monthly' ? plan : { kind: 'monthly', nth: 1, weekday: today };
                  const { every: _e, from: _f, ...rest } = base as any;
                  setPlan(every > 1 ? { ...rest, every, ...start() } : rest);
                }}>
                {everyOptions('admin_panel.plan_month_every', [1, 2, 3, 4, 6, 12])}
              </select>
            </Choice>
            {plan?.kind === 'monthly' && (
              <div className="ml-9 space-y-1">
                <label className="flex flex-wrap items-center gap-2 min-h-[44px] cursor-pointer">
                  <input type="radio" name="plan-month" className="w-6 h-6 accent-kid-green" checked={plan.nth != null}
                    onChange={() => setPlan({ ...plan, day: undefined, nth: 1, weekday: today })} />
                  {i18n.t('admin_panel.plan_the')}
                  <select aria-label={i18n.t('admin_panel.plan_which')} className={pickCls} value={plan.nth ?? 1}
                    onChange={(e) => setPlan({ ...plan, day: undefined, nth: Number(e.target.value), weekday: plan.weekday ?? today })}>
                    {[1, 2, 3, 4, -1].map((n) => <option key={n} value={n}>{i18n.t(n === -1 ? 'admin_panel.plan_nth_last' : `admin_panel.plan_nth_${n}`)}</option>)}
                  </select>
                  <select aria-label={i18n.t('admin_panel.plan_weekday')} className={pickCls} value={plan.weekday ?? today}
                    onChange={(e) => setPlan({ ...plan, day: undefined, nth: plan.nth ?? 1, weekday: Number(e.target.value) })}>
                    {[1, 2, 3, 4, 5, 6, 7].map((d) => <option key={d} value={d}>{longDays[d]}</option>)}
                  </select>
                </label>
                <label className="flex flex-wrap items-center gap-2 min-h-[44px] cursor-pointer">
                  <input type="radio" name="plan-month" className="w-6 h-6 accent-kid-green" checked={plan.day != null}
                    onChange={() => setPlan({ ...plan, nth: undefined, weekday: undefined, day: new Date().getDate() })} />
                  {i18n.t('admin_panel.plan_on_date')}
                  <input type="number" min={1} max={31} aria-label={i18n.t('admin_panel.plan_on_date')} className={inputCls}
                    value={plan.day ?? ''} placeholder="1"
                    onFocus={() => plan.day == null && setPlan({ ...plan, nth: undefined, weekday: undefined, day: 1 })}
                    onChange={(e) => setPlan({ ...plan, nth: undefined, weekday: undefined, day: Math.min(31, days(e.target.value)) })} />
                  {i18n.t('admin_panel.plan_date_unit')}
                </label>
              </div>
            )}
            <Choice name="plan" checked={plan?.kind === 'every'} onSelect={() => setPlan({ kind: 'every', every: 2, ...start() })}>
              {i18n.t('admin_panel.plan_every')}
              <input type="number" min={1} aria-label={i18n.t('admin_panel.plan_every')} className={inputCls}
                value={plan?.kind === 'every' ? plan.every : ''} placeholder="2"
                onFocus={() => plan?.kind !== 'every' && setPlan({ kind: 'every', every: 2, ...start() })}
                onChange={(e) => setPlan({ kind: 'every', every: days(e.target.value), ...(plan?.kind === 'every' && plan.from ? { from: plan.from } : start()) })} />
              {i18n.t('admin_panel.plan_every_unit')}
            </Choice>
            {!forTemplate && plan && 'from' in plan && plan.from && (plan.kind === 'every' || (plan.every || 1) > 1) && (
              <label className="flex flex-wrap items-center gap-2 ml-9 min-h-[44px] text-base">
                {i18n.t('admin_panel.plan_from')}
                <input type="date" className={pickCls} value={plan.from}
                  onChange={(e) => e.target.value && setPlan({ ...plan, from: e.target.value })} />
              </label>
            )}
          </div>
        )}
        {nextChoices.length > 0 && (
          <Choice name="repeat" checked={mode === 'next'} onSelect={() => setMode('next')}>
            {i18n.t('admin_panel.repeat_next')}
            <select aria-label={i18n.t('admin_panel.repeat_next')} className={`${fieldCls} !w-auto max-w-full`}
              value={mode === 'next' ? data.next_series_id || '' : ''}
              onFocus={() => mode !== 'next' && setMode('next')}
              onChange={(e) => onChange({ ...fieldsForRepeatMode('next', data), next_series_id: e.target.value })}>
              {mode !== 'next' && <option value="">…</option>}
              {nextChoices.map((c) => <option key={c.series} value={c.series}>{c.title}</option>)}
            </select>
          </Choice>
        )}
        {mode === 'next' && (
          <label className="flex flex-wrap items-center gap-2 ml-9 min-h-[44px] text-base">
            {i18n.t('admin_panel.next_ready_after')}
            <input type="number" min={0} aria-label={i18n.t('admin_panel.next_ready_after')} className={inputCls}
              value={data.next_wait_hours ?? 0}
              onChange={(e) => onChange({ next_wait_hours: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
            {i18n.t('admin_panel.hours_unit')}
          </label>
        )}
      </fieldset>

      {(mode === 'now' || mode === 'after' || mode === 'plan') && assignee && (
        <fieldset>
          <legend className="block text-base font-bold mb-1">{i18n.t('admin_panel.next_question')}</legend>
          <Choice name="next" checked={data.auto_available === false} onSelect={() => onChange({ auto_available: false })}>
            {i18n.t('admin_panel.next_same').replace('{{name}}', assignee.name)}
          </Choice>
          <Choice name="next" checked={data.auto_available !== false} onSelect={() => onChange({ auto_available: true })}>
            {i18n.t('admin_panel.next_anyone')}
          </Choice>
        </fieldset>
      )}

      <div className="rounded-kid-tile bg-kid-green-bg dark:bg-green-950/40 px-4 py-3" aria-live="polite">
        <p className="font-bold mb-1">{i18n.t('admin_panel.summary_title')}</p>
        {repeatSummary({
          mode, afterDays: data.recurrence_days, withinDays: within,
          assigneeName: assignee?.name, nextAnyone: data.auto_available !== false,
          nextTitle, waitHours: data.next_wait_hours,
          eachNames: assigneeNames.length >= 2 ? assigneeNames : undefined,
          schedule: data.repeat_schedule, weekdayNames: weekday('long'),
          formatDate: (d) => new Date(`${d}T12:00:00`).toLocaleDateString(current_language, { day: 'numeric', month: 'long' }),
        }).map((line) => <p key={line} className="text-base">{line}</p>)}
      </div>

      {!forTemplate && <div>
        <label htmlFor="task-due-this-time" className="block text-base font-bold mb-1">{i18n.t('admin_panel.due_this_time_label')}</label>
        <input id="task-due-this-time" type="date" className={fieldCls}
          value={toDateInput(data.due_date)}
          // 20:00 local on the chosen day, like the deadlines the server sets.
          onChange={(e) => onChange({ due_date: e.target.value ? new Date(`${e.target.value}T20:00:00`) : undefined })} />
        <p className="text-xs text-kid-text-muted mt-1">{i18n.t('admin_panel.due_this_time_help')}</p>
      </div>}
    </div>
  );
};

export default RepeatSettings;
